import { useQuery } from '@tanstack/react-query'
import { localeFor } from '@/lib/locale'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import { ArrowLeft, Plus, Trash2, Link2, Download, Tags, Check, ChevronDown } from 'lucide-react'
import { toast } from 'sonner'
import { useInvoices } from '@/hooks/use-invoices'
import { supabase } from '@/lib/supabase'
import type { LineInput } from '@/hooks/use-quotes'
import { useClients, clientName } from '@/hooks/use-clients'
import { useCompany } from '@/hooks/use-company'
import { usePricing } from '@/hooks/use-pricing'
import { money, taxRate, taxRatePct } from '@/lib/format'
import { UnsavedGuard } from '@/components/unsaved-guard'
import { Page, PageHeader, Card, CardHeader, StatusPill, DetailSkeleton, DocumentBuilder, BuilderBack, btnPrimary, btnSecondary } from '@/components/rm'
import { ThreadStrip } from '@/components/thread-strip'
import { ActivityTimeline } from '@/components/activity-timeline'
import { useInvoiceActivity } from '@/hooks/use-activity'
import { PriceListPicker } from '@/components/price-list-picker'
import { Field, Input, Select } from '@/components/rm/form'
import { phFormat } from '@/lib/ph'

export const Route = createFileRoute('/_app/invoices_/$id')({
  component: InvoiceBuilder,
})

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100
const menuItem = 'block w-full px-3.5 py-2 text-left text-[13px] hover:bg-token-surface'

function InvoiceBuilder() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const { invoice, items, payments, isLoadingDetail, save, addPayment, setStatus } = useInvoices(id)
  const [sendOpen, setSendOpen] = useState(false)
  const [attachPhotos, setAttachPhotos] = useState(true)
  const [sending, setSending] = useState(false)
  const { clients } = useClients()
  const { company } = useCompany()
  const { items: catalogue } = usePricing()
  const [pickerOpen, setPickerOpen] = useState(false)

  // Unsaved-changes tracking (spec 005): signature of the editable document.
  const savedSig = useRef('')
  const sigOf = (v: unknown) => JSON.stringify(v)

  const [loaded, setLoaded] = useState<number | null>(null)
  const [title, setTitle] = useState('')
  const [clientId, setClientId] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [gst, setGst] = useState(false)
  const [lines, setLines] = useState<LineInput[]>([])
  const [payAmt, setPayAmt] = useState<number | ''>('')
  const [payMethod, setPayMethod] = useState('bank_transfer')
  // GCash is offered only for Philippine companies (manual record for now).
  const country = (company?.country ?? '').trim().toLowerCase()
  const isPH = country === 'ph' || country.includes('philippin')

  if (invoice && invoice.id !== loaded) {
    const loadedTitle = invoice.title ?? ''
    const loadedClientId = invoice.client_id ?? ''
    const loadedDueDate = invoice.due_date ?? ''
    // Default GST on: existing tax, OR a fresh/empty invoice when the company is GST-registered.
    const loadedGst = (invoice.tax ?? 0) > 0 || ((invoice.subtotal ?? 0) === 0 && (company?.gst_registered ?? true))
    const loadedLines: LineInput[] = items.length
      ? items.map((it) => ({ description: it.description, quantity: Number(it.quantity), unit_price: Number(it.unit_price) }))
      : [{ description: '', quantity: 1, unit_price: 0 }]

    setLoaded(invoice.id)
    setTitle(loadedTitle)
    setClientId(loadedClientId)
    setDueDate(loadedDueDate)
    setGst(loadedGst)
    setLines(loadedLines)
    savedSig.current = sigOf({
      title: loadedTitle, clientId: loadedClientId, dueDate: loadedDueDate, gst: loadedGst, lines: loadedLines,
    })
  }

  // MUST stay above the early returns below. React counts hooks per render, so a hook placed after
  // them runs on the loaded render but not on the skeleton render — which is exactly React error
  // #310 ("rendered more hooks than during the previous render"). It crashed any COLD load of an
  // invoice; opening one from the list hid it, because the detail was already cached and the
  // skeleton branch never rendered. `enabled` already guards the query itself.
  const proofCount = useQuery({
    queryKey: ['proof-count', invoice?.task_id],
    queryFn: async () => {
      const { count } = await supabase.from('task_attachment').select('id', { count: 'exact', head: true })
        .eq('task_id', invoice!.task_id!).in('kind', ['proof', 'image'])
      return count ?? 0
    },
    enabled: !!invoice?.task_id,
  })

  if (isLoadingDetail && !invoice) {
    return (
      <Page>
        <DetailSkeleton />
      </Page>
    )
  }
  if (!invoice) {
    return (
      <Page>
        <PageHeader title="Invoice not found" />
        <button className={btnSecondary + ' mt-4'} onClick={() => navigate({ to: '/invoices' })}>
          <ArrowLeft className="h-4 w-4" /> Back to invoices
        </button>
      </Page>
    )
  }

  const editable = invoice.status === 'draft' || invoice.status === 'sent' || invoice.status === 'partial'
  const currentSig = sigOf({ title, clientId, dueDate, gst, lines })
  const isDirty = editable && loaded === invoice.id && currentSig !== savedSig.current
  const subtotal = round2(lines.reduce((s, l) => s + (l.quantity || 0) * (l.unit_price || 0), 0))
  const tax = gst ? round2(subtotal * taxRate(company)) : 0
  // Spec 025 — mirrors the save path: on a progress claim, retention comes off after GST. These two
  // must stay in step, or the screen shows one total and the saved row holds another.
  const isClaim = !!invoice.claim_number && !invoice.is_retention_release
  const retention = isClaim ? round2(subtotal * ((Number(invoice.retention_pct) || 0) / 100)) : 0
  const total = round2(subtotal + tax - retention)
  const paid = Number(invoice.amount_paid) || 0
  const balance = round2(total - paid)
  const cur = invoice.currency

  const setLine = (i: number, p: Partial<LineInput>) => setLines(lines.map((l, idx) => (idx === i ? { ...l, ...p } : l)))

  const doSave = async () => {
    try {
      await save.mutateAsync({
        id: invoice.id,
        title: title.trim() || null,
        client_id: clientId || null,
        due_date: dueDate || null,
        items: lines.filter((l) => l.description.trim()),
        applyGst: gst,
      })
      savedSig.current = currentSig
      toast.success('Saved')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed')
    }
  }
  const recordPayment = async (amount: number) => {
    if (!amount || amount <= 0) {
      toast.error('Enter an amount.')
      return
    }
    try {
      await addPayment.mutateAsync({ invoice, amount: round2(amount), method: payMethod })
      setPayAmt('')
      toast.success('Payment recorded')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed')
    }
  }

  const sendViaMySerbisyo = async () => {
    setSendOpen(false)
    setSending(true)
    const withPhotos = attachPhotos && (proofCount.data ?? 0) > 0
    const { data, error } = await supabase.functions.invoke('send-invoice', { body: { invoiceId: invoice.id, origin: window.location.origin, attachPhotos: withPhotos } })
    setSending(false)
    if (error) {
      const ctx = (error as { context?: Response }).context
      const b = ctx && typeof ctx.json === 'function' ? await ctx.json().catch(() => null) : null
      return toast.error(b?.error ? (b.detail ? `${b.error}: ${b.detail}` : b.error) : 'Could not send invoice')
    }
    if (invoice.share_token) navigator.clipboard.writeText(`${window.location.origin}/i/${invoice.share_token}`)
    if (invoice.status === 'draft') setStatus.mutate({ id: invoice.id, status: 'sent' })
    toast.success(`Invoice emailed to ${(data as { to?: string })?.to ?? 'the customer'} · link copied`)
  }

  return (
    <DocumentBuilder
      back={<BuilderBack label="Invoices" onClick={() => navigate({ to: '/invoices' })} />}
      strip={<ThreadStrip anchor={{ invoiceId: invoice.id }} current="invoice" currentInvoiceId={invoice.id} />}
      actions={
        <>
          <StatusPill status={invoice.status} />
          <span className="text-[12.5px] text-token-secondary tabular-nums">Balance {money(balance, cur)}</span>
            {invoice.share_token && (
              <>
                <button className={btnSecondary} onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/i/${invoice.share_token}`); toast.success('Customer link copied') }}>
                  <Link2 className="h-4 w-4" /> Copy link
                </button>
                <button className={btnSecondary} onClick={() => window.open(`/i/${invoice.share_token}`, '_blank')}>
                  <Download className="h-4 w-4" /> PDF
                </button>
              </>
            )}
            {editable && (
              <button className={btnSecondary} onClick={doSave} disabled={save.isPending}>
                {save.isPending ? 'Saving…' : 'Save'}
              </button>
            )}
            {invoice.xero_invoice_id ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-token-success-surface px-3 py-1 text-[12px] font-bold text-token-success">
                <Check className="h-3.5 w-3.5" /> Xero{invoice.xero_invoice_number ? ` · ${invoice.xero_invoice_number}` : ''}
              </span>
            ) : (
              <div className="relative">
                <button className={btnSecondary} onClick={() => setSendOpen((v) => !v)} disabled={sending}>
                  {sending ? 'Sending…' : 'Send'} <ChevronDown className="h-4 w-4" />
                </button>
                {sendOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setSendOpen(false)} />
                    <div className="absolute right-0 z-20 mt-1 w-60 overflow-hidden rounded-xl border border-token-subtle bg-token-surface py-1 shadow-lg">
                      <button className={menuItem} onClick={sendViaMySerbisyo}>
                        <div className="font-semibold text-token-primary">Send via MySerbisyo</div>
                        <div className="text-[11.5px] text-token-secondary">Email the invoice & copy link</div>
                      </button>
                      {(proofCount.data ?? 0) > 0 && (
                        <label className="flex items-center gap-2 px-3 pb-2 pt-1 text-[12px] text-token-secondary">
                          <input type="checkbox" checked={attachPhotos} onChange={(e) => setAttachPhotos(e.target.checked)} className="h-3.5 w-3.5 accent-brand" />
                          Attach job photos ({proofCount.data})
                        </label>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            {invoice.status !== 'paid' && balance > 0 && (
              <button className={btnPrimary} onClick={() => recordPayment(balance)} disabled={addPayment.isPending}>
                Mark paid
              </button>
            )}
        </>
      }
    >
      <UnsavedGuard when={isDirty} />
      <div className="space-y-5">
        {/* 011 US1 — sent, opened, chased, paid, synced. The DocumentBuilder shell (006 T010) owns
            the status pill, the balance and the thread strip now, so only the timeline moves in. */}
        <InvoiceActivity invoiceId={invoice.id} />
        <Card>
          <CardHeader title="Details" />
          <div className="grid grid-cols-1 gap-3 px-5 py-4 md:grid-cols-2">
            <Field label="Title" className="md:col-span-2">
              <Input value={title} disabled={!editable} onChange={(e) => setTitle(e.target.value)} />
            </Field>
            <Field label="Client">
              <Select value={clientId} disabled={!editable} onChange={(e) => setClientId(e.target.value)}>
                <option value="">No client</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {clientName(c)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Due date">
              <Input type="date" value={dueDate} disabled={!editable} onChange={(e) => setDueDate(e.target.value)} />
            </Field>
          </div>
        </Card>

        <Card>
          <CardHeader title="Line items" />
          <div className="space-y-2 overflow-x-auto px-5 py-4">
            {lines.map((l, i) => (
              <div key={i} className="flex min-w-[460px] items-center gap-2">
                <Input value={l.description} disabled={!editable} onChange={(e) => setLine(i, { description: e.target.value })} placeholder="Description" className="flex-1" />
                <Input type="number" value={Number.isFinite(l.quantity) ? l.quantity : ''} disabled={!editable} onChange={(e) => setLine(i, { quantity: e.target.valueAsNumber || 0 })} className="w-20" placeholder="Qty" />
                <Input type="number" value={Number.isFinite(l.unit_price) ? l.unit_price : ''} disabled={!editable} onChange={(e) => setLine(i, { unit_price: e.target.valueAsNumber || 0 })} className="w-28" placeholder="Unit $" />
                <span className="w-24 text-right text-[13px] tabular-nums text-token-primary">{money((l.quantity || 0) * (l.unit_price || 0), cur)}</span>
                {editable && (
                  <button onClick={() => setLines(lines.filter((_, idx) => idx !== i))} className="text-token-muted hover:text-rose-500">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            ))}
            {editable && (
              <div className="mt-2 flex items-center gap-4">
                <button className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-token-primary hover:text-token-primary" onClick={() => setLines([...lines, { description: '', quantity: 1, unit_price: 0 }])}>
                  <Plus className="h-3.5 w-3.5" /> Add line
                </button>
                <button className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-token-secondary hover:text-token-primary" onClick={() => setPickerOpen(true)}>
                  <Tags className="h-3.5 w-3.5" /> From price list
                </button>
              </div>
            )}

            <div className="mt-4 flex flex-col items-end gap-1.5 border-t border-token-subtle pt-3">
              <div className="flex w-full max-w-[280px] items-center justify-between text-[13px]">
                <span className="text-token-secondary">Subtotal</span>
                <span className="tabular-nums text-token-primary">{money(subtotal, cur)}</span>
              </div>
              <label className="flex w-full max-w-[280px] items-center justify-between text-[13px] text-token-secondary">
                <span className="inline-flex items-center gap-2">
                  <input type="checkbox" checked={gst} disabled={!editable} onChange={(e) => setGst(e.target.checked)} className="h-3.5 w-3.5" />
                  {localeFor(company).taxLabel} ({taxRatePct(company)}%)
                </span>
                <span className="tabular-nums text-token-primary">{money(tax, cur)}</span>
              </label>
              {retention > 0 && (
                <div className="flex w-full max-w-[280px] items-center justify-between text-[13px]">
                  <span className="text-token-secondary">Less retention ({Number(invoice.retention_pct) || 0}%)</span>
                  <span className="tabular-nums text-token-primary">−{money(retention, cur)}</span>
                </div>
              )}
              <div className="flex w-full max-w-[280px] items-center justify-between text-[14px] font-semibold">
                <span className="text-token-primary">Total</span>
                <span className="tabular-nums text-token-primary">{money(total, cur)}</span>
              </div>
              <div className="flex w-full max-w-[280px] items-center justify-between text-[13px]">
                <span className="text-token-secondary">Paid</span>
                <span className="tabular-nums text-token-primary">{money(paid, cur)}</span>
              </div>
              <div className="flex w-full max-w-[280px] items-center justify-between text-[14px] font-semibold">
                <span className="text-token-primary">Balance due</span>
                <span className={`tabular-nums ${balance > 0 ? 'text-rose-600' : 'text-emerald-600'}`}>{money(balance, cur)}</span>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <CardHeader title="Payments" count={payments.length} />
          {invoice.status !== 'void' && (
            <div className="flex flex-wrap items-center gap-2 border-b border-token-subtle px-5 py-3">
              <Input type="number" value={payAmt} onChange={(e) => setPayAmt(e.target.valueAsNumber || '')} placeholder="Amount" className="w-32" />
              <Select value={payMethod} onChange={(e) => setPayMethod(e.target.value)} className="w-auto">
                <option value="bank_transfer">Bank transfer</option>
                <option value="card">Card</option>
                <option value="cash">Cash</option>
                {isPH && <option value="gcash">GCash</option>}
                <option value="other">Other</option>
              </Select>
              <button className={btnSecondary} onClick={() => recordPayment(typeof payAmt === 'number' ? payAmt : 0)} disabled={addPayment.isPending}>
                Record
              </button>
            </div>
          )}
          {payments.length === 0 ? (
            <p className="px-5 py-8 text-center text-[13px] text-token-secondary">No payments recorded.</p>
          ) : (
            <ul className="divide-y divide-token-subtle">
              {payments.map((p) => (
                <li key={p.id} className="flex items-center justify-between px-5 py-2.5 text-[13px]">
                  <span className="text-token-secondary">
                    {p.paid_at ? phFormat(new Date(p.paid_at), 'date') : ''} · {p.method ?? 'payment'}
                  </span>
                  <span className="font-medium tabular-nums text-token-primary">{money(Number(p.amount), cur)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <PriceListPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        catalogue={catalogue}
        currency={cur}
        onPick={(c) => {
          setLines((prev) => [...prev.filter((l) => l.description.trim()), { description: c.name, quantity: 1, unit_price: Number(c.sell_price) }])
          setPickerOpen(false)
        }}
      />
    </DocumentBuilder>
  )
}

/** 011 T005. Separate component so the activity query does not re-render the invoice editor. */
function InvoiceActivity({ invoiceId }: { invoiceId: number }) {
  const { data = [], isLoading } = useInvoiceActivity(invoiceId)
  return <ActivityTimeline entries={data} isLoading={isLoading} />
}
